import { create } from 'zustand';
import { readLS, writeLS } from '@/services/storage/local';
import type { MarketplaceProduct } from '@/features/marketplace/marketplaceApi';
import { calcBreakdown, type CheckoutBreakdown } from '@/features/marketplace/lib';

export type CartLine = { product: MarketplaceProduct; qty: number };

type CartState = {
  lines: CartLine[];
  add: (product: MarketplaceProduct) => boolean;
  remove: (productId: string) => void;
  setQty: (productId: string, qty: number) => void;
  clear: () => void;
  total: () => number;
  breakdown: () => CheckoutBreakdown;
};

const KEY = 'canabiz-cart-v2';

const persisted = readLS<CartLine[]>(KEY, []);

export const useCartStore = create<CartState>((set, get) => ({
  lines: persisted,
  add: (product) => {
    const lines = get().lines;
    if (lines.some((line) => line.product.association_id !== product.association_id)) return false;
    const existing = lines.find((l) => l.product.id === product.id);
    const next = existing
      ? lines.map((l) => (l.product.id === product.id ? { ...l, qty: l.qty + 1 } : l))
      : [...lines, { product, qty: 1 }];
    writeLS(KEY, next);
    set({ lines: next });
    return true;
  },
  remove: (productId) => {
    const next = get().lines.filter((l) => l.product.id !== productId);
    writeLS(KEY, next);
    set({ lines: next });
  },
  setQty: (productId, qty) => {
    if (qty <= 0) return get().remove(productId);
    const next = get().lines.map((l) => (l.product.id === productId ? { ...l, qty } : l));
    writeLS(KEY, next);
    set({ lines: next });
  },
  clear: () => {
    writeLS(KEY, []);
    set({ lines: [] });
  },
  total: () => get().lines.reduce((s, l) => s + l.product.price * l.qty, 0),
  breakdown: () => calcBreakdown(get().lines.reduce((s, l) => s + l.product.price * l.qty, 0)),
}));

export const useCartCount = () => useCartStore((s) => s.lines.reduce((acc, l) => acc + l.qty, 0));
